import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import {
  RiArrowLeftLine,
  RiBusLine,
  RiUserLine,
  RiCalendarLine,
  RiMapPinLine,
  RiCloseLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiMoneyRupeeCircleLine,
  RiAddLine,
  RiSubtractLine,
  RiEditLine,
  RiMapPinTimeLine,
  RiTimeLine,
  RiUserAddLine,
  RiUserUnfollowLine,
  RiInformationLine,
  RiAlertLine,
} from "react-icons/ri";
import BusLayout from "./BusLayouts/BusLayout";
import BusBoardingPoints from "./BusBoardingPoints";
import "./BusSeatSelection.css";

const BusSeatSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bus, searchQuery } = location.state || {};

  const [seatLayoutData, setSeatLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerCount, setPassengerCount] = useState(1);
  const [showBoardingPoints, setShowBoardingPoints] = useState(false);
  const [selectedBoardingPoint, setSelectedBoardingPoint] = useState(null);
  const [selectedDroppingPoint, setSelectedDroppingPoint] = useState(null);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const [seatTypes, setSeatTypes] = useState({});
  const [fareSummary, setFareSummary] = useState({
    baseFare: 0,
    convenienceFee: 0,
    total: 0,
  });

  const isUpdating = useRef(false);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-IN", options);
  };

  // Format time function
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Initialize on mount
  useEffect(() => {
    if (!bus || !searchQuery) {
      navigate("/bus/results");
      return;
    }
    fetchSeatLayout();
  }, [bus, searchQuery, navigate]);

  // Initialize passenger details
  useEffect(() => {
    const initialDetails = Array.from(
      { length: passengerCount },
      (_, index) => ({
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
        seatNumber: selectedSeats[index]?.id || "",
      })
    );
    setPassengerDetails(initialDetails);
  }, [user, passengerCount]);

  // Update seat types from layout data
  useEffect(() => {
    if (seatLayoutData && seatLayoutData.seats) {
      const types = {};
      seatLayoutData.seats.forEach((seat) => {
        if (seat.available) {
          const type = seat.sleeper
            ? seat.SeatType === 1
              ? "UPPER_SLEEPER"
              : "LOWER_SLEEPER"
            : "SEATER";
          types[type] = (types[type] || 0) + 1;
        }
      });
      setSeatTypes(types);
    }
  }, [seatLayoutData]);

  // Handle seat selection changes
  useEffect(() => {
    if (isUpdating.current || selectedSeats.length === 0) return;

    isUpdating.current = true;

    // Update passenger count to match selected seats
    if (selectedSeats.length !== passengerCount) {
      setPassengerCount(selectedSeats.length);

      if (selectedSeats.length > passengerDetails.length) {
        // Add new passenger forms
        const passengersToAdd = selectedSeats.length - passengerDetails.length;
        const newPassengers = Array.from(
          { length: passengersToAdd },
          (_, index) => {
            const seatIndex = passengerDetails.length + index;
            return {
              firstName: "",
              lastName: "",
              age: "",
              title: "Mr",
              idProof: "",
              idProofNumber: "",
              email: user?.email || "",
              phone: user?.phoneNumber || "",
              seatNumber: selectedSeats[seatIndex]?.id || "",
            };
          }
        );
        setPassengerDetails((prev) => [...prev, ...newPassengers]);
      } else if (selectedSeats.length < passengerDetails.length) {
        // Remove extra passenger forms
        setPassengerDetails((prev) => prev.slice(0, selectedSeats.length));
      }
    }

    // Update seat numbers in passenger details
    const updatedDetails = passengerDetails.map((detail, index) => ({
      ...detail,
      seatNumber: selectedSeats[index]?.id || detail.seatNumber,
    }));
    setPassengerDetails(updatedDetails);

    // Update fare summary
    updateFareSummary();

    setTimeout(() => {
      isUpdating.current = false;
    }, 100);
  }, [selectedSeats]);

  // Fetch seat layout from API
  const fetchSeatLayout = async () => {
    setLoading(true);
    setError(null);
    try {
      const requestData = {
        CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        Source: searchQuery.fromStation,
        Destination: searchQuery.toStation,
        DateOfJourney: searchQuery.travelDate,
        InventoryType: bus.inventoryType,
        RouteScheduleId: bus.routeScheduleId,
      };
      const url = `${config.GETBUSSEATS}/${bus.supplierId}`;

      const response = await apiService.post(url, requestData);
      if (response) {
        setSeatLayoutData(response);
      } else {
        setError("Failed to load seat layout. Please try again.");
      }
    } catch (err) {
      setError("Failed to fetch seat layout. Please try again.");
      console.error("Seat layout error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle seat selection
  const handleSeatSelect = (seat) => {
    if (!seat.available || seat.bookedBy) return;

    const isAlreadySelected = selectedSeats.some((s) => s.id === seat.id);

    if (isAlreadySelected) {
      // Deselect seat
      const newSelectedSeats = selectedSeats.filter((s) => s.id !== seat.id);
      setSelectedSeats(newSelectedSeats);

      // Clear boarding points only if ALL seats are deselected
      if (newSelectedSeats.length === 0) {
        setSelectedBoardingPoint(null);
        setSelectedDroppingPoint(null);
      }
    } else {
      // Select seat
      const newSelectedSeats = [...selectedSeats, seat];
      setSelectedSeats(newSelectedSeats);
    }
  };

  // Update fare summary
  const updateFareSummary = () => {
    const baseFare = selectedSeats.reduce((total, seat) => {
      return total + (parseFloat(seat.totalPriceWithTaxes) || 0);
    }, 0);

    const convenienceFee = selectedSeats.reduce((total, seat) => {
      return total + (parseFloat(seat.convenienceFee) || 100);
    }, 0);

    const total = baseFare + convenienceFee;

    setFareSummary({
      baseFare,
      convenienceFee,
      total,
    });
  };

  // Handle passenger count change
  const handlePassengerCountChange = (count) => {
    if (count < 1 || count > 6) return;

    isUpdating.current = true;

    if (count < passengerCount) {
      setPassengerCount(count);

      // If reducing passenger count, also reduce selected seats if needed
      if (selectedSeats.length > count) {
        const seatsToRemove = selectedSeats.length - count;
        setSelectedSeats((prev) => prev.slice(0, -seatsToRemove));
      }

      // Reduce passenger details
      setPassengerDetails((prev) => prev.slice(0, count));
    } else if (count > passengerCount) {
      setPassengerCount(count);

      // Add new passenger details
      const passengersToAdd = count - passengerDetails.length;
      const newPassengers = Array.from({ length: passengersToAdd }, () => ({
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
        seatNumber: "",
      }));

      setPassengerDetails((prev) => [...prev, ...newPassengers]);
    }

    setTimeout(() => {
      isUpdating.current = false;
    }, 100);
  };

  // Handle passenger detail change
  const handlePassengerDetailChange = (index, field, value) => {
    const updatedDetails = [...passengerDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };
    setPassengerDetails(updatedDetails);
  };

  // Handle email change for all passengers
  const handleEmailChange = (email) => {
    const updatedDetails = passengerDetails.map((detail) => ({
      ...detail,
      email: email,
    }));
    setPassengerDetails(updatedDetails);
  };

  // Handle phone change for all passengers
  const handlePhoneChange = (phone) => {
    const updatedDetails = passengerDetails.map((detail) => ({
      ...detail,
      phone: phone,
    }));
    setPassengerDetails(updatedDetails);
  };

  // Check if all passenger details are filled
  const areAllPassengerDetailsFilled = () => {
    return passengerDetails.every(
      (passenger) =>
        passenger.firstName &&
        passenger.lastName &&
        passenger.age &&
        passenger.idProof &&
        passenger.email &&
        passenger.phone
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num).toLocaleString("en-IN")}`;
  };

  // Handle boarding point selection
  const handleChangeBoardingPoints = () => {
    setShowBoardingPoints(true);
  };

  const handleBoardingPointsSubmit = async (boardingPoint, droppingPoint) => {
    setSelectedBoardingPoint(boardingPoint);
    setSelectedDroppingPoint(droppingPoint);
    setShowBoardingPoints(false);
  };

  const handleBoardingPointsClose = () => {
    setShowBoardingPoints(false);
  };

  // Proceed to payment
  const handleProceedToPayment = () => {
    if (!selectedBoardingPoint || !selectedDroppingPoint) {
      alert("Please select both boarding and dropping points first.");
      return;
    }

    if (!areAllPassengerDetailsFilled()) {
      alert("Please fill all passenger details before proceeding.");
      return;
    }

    if (selectedSeats.length !== passengerDetails.length) {
      alert("Please ensure each passenger has a seat assigned.");
      return;
    }

    navigate("/bus/payment", {
      state: {
        bus,
        searchQuery,
        selectedSeats: selectedSeats.map((seat) => ({
          ...seat,
          totalPriceWithTaxes: parseFloat(seat.totalPriceWithTaxes) || 0,
          convenienceFee: parseFloat(seat.convenienceFee) || 100,
          serviceTaxAmount: parseFloat(seat.serviceTaxAmount) || 0,
        })),
        passengerCount,
        passengerDetails,
        selectedBoardingPoint,
        selectedDroppingPoint,
        totalAmount: fareSummary.total,
        baseFare: fareSummary.baseFare,
        convenienceFee: fareSummary.convenienceFee,
      },
    });
  };

  // Clear all seats
  const handleClearAllSeats = () => {
    setSelectedSeats([]);
    setSelectedBoardingPoint(null);
    setSelectedDroppingPoint(null);
    setPassengerCount(1);
    setPassengerDetails([
      {
        firstName: "",
        lastName: "",
        age: "",
        title: "Mr",
        idProof: "",
        idProofNumber: "",
        email: user?.email || "",
        phone: user?.phoneNumber || "",
        seatNumber: "",
      },
    ]);
    setFareSummary({
      baseFare: 0,
      convenienceFee: 0,
      total: 0,
    });
  };

  // Sync seats with forms
  const syncSeatsWithForms = () => {
    if (selectedSeats.length > passengerDetails.length) {
      // Add missing forms
      const formsToAdd = selectedSeats.length - passengerDetails.length;
      const newPassengers = Array.from({ length: formsToAdd }, (_, index) => {
        const seatIndex = passengerDetails.length + index;
        return {
          firstName: "",
          lastName: "",
          age: "",
          title: "Mr",
          idProof: "",
          idProofNumber: "",
          email: user?.email || "",
          phone: user?.phoneNumber || "",
          seatNumber: selectedSeats[seatIndex]?.id || "",
        };
      });
      setPassengerDetails((prev) => [...prev, ...newPassengers]);
    } else if (selectedSeats.length < passengerDetails.length) {
      // Remove extra forms
      setPassengerDetails((prev) => prev.slice(0, selectedSeats.length));
    }
    setPassengerCount(selectedSeats.length);
  };

  // Redirect if no bus data
  if (!bus || !searchQuery) {
    return (
      <div id="layout-wrapper">
        <Header />
        <div className="main-content">
          <div className="container-fluid">
            <div className="alert alert-danger">
              Invalid bus selection. Please go back and try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="layout-wrapper">
      <Header />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Header Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h1 className="mb-1">Select Your Seats</h1>
                    <p className="text-muted mb-0">
                      Choose seats and provide passenger details
                    </p>
                  </div>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                  >
                    <RiArrowLeftLine className="me-1" />
                    Back to Buses
                  </button>
                </div>
              </div>
            </div>

            {/* Bus Info Card */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <div className="bus-info-item">
                      <div className="bus-info-icon">
                        <RiBusLine />
                      </div>
                      <div className="bus-info-content">
                        <div className="bus-info-label">Bus Operator</div>
                        <div className="bus-info-value">{bus.operatorName}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="bus-info-item">
                      <div className="bus-info-icon">
                        <RiMapPinLine />
                      </div>
                      <div className="bus-info-content">
                        <div className="bus-info-label">Route</div>
                        <div className="bus-info-value">
                          {searchQuery.fromStation} → {searchQuery.toStation}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="bus-info-item">
                      <div className="bus-info-icon">
                        <RiCalendarLine />
                      </div>
                      <div className="bus-info-content">
                        <div className="bus-info-label">Travel Date</div>
                        <div className="bus-info-value">
                          {formatDate(searchQuery.travelDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="bus-info-item">
                      <div className="bus-info-icon">
                        <RiUserLine />
                      </div>
                      <div className="bus-info-content">
                        <div className="bus-info-label">Passengers</div>
                        <div className="passenger-controls">
                          <button
                            className="passenger-control-btn"
                            onClick={() =>
                              handlePassengerCountChange(passengerCount - 1)
                            }
                            disabled={passengerCount <= 1}
                          >
                            <RiSubtractLine />
                          </button>
                          <span className="passenger-count">
                            {passengerCount}
                          </span>
                          <button
                            className="passenger-control-btn"
                            onClick={() =>
                              handlePassengerCountChange(passengerCount + 1)
                            }
                            disabled={passengerCount >= 6}
                          >
                            <RiAddLine />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Main Content - Seat Layout & Passenger Forms */}
              <div className="col-lg-8">
                {/* Seat Layout Card */}
                <div className="card mb-4">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Seats - {bus.busType}</h5>
                      <div className="d-flex align-items-center gap-2">
                        <div className="selection-badge">
                          {selectedSeats.length} Selected
                        </div>
                        {selectedSeats.length > 0 && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={handleClearAllSeats}
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {loading && (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading seat layout...</p>
                      </div>
                    )}

                    {error && (
                      <div className="text-center py-5">
                        <div className="alert alert-danger">
                          <p>{error}</p>
                          <button
                            className="btn btn-primary mt-2"
                            onClick={fetchSeatLayout}
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    )}

                    {!loading && !error && seatLayoutData && (
                      <>
                        {/* Seat Legend */}
                        <div className="seat-legend mb-4">
                          <div className="seat-legend-title">Seat Legend</div>
                          <div className="seat-legend-items">
                            <div className="seat-legend-item">
                              <div className="legend-seat available"></div>
                              <span>Available</span>
                            </div>
                            <div className="seat-legend-item">
                              <div className="legend-seat selected"></div>
                              <span>Selected</span>
                            </div>
                            <div className="seat-legend-item">
                              <div className="legend-seat booked"></div>
                              <span>Booked</span>
                            </div>
                            <div className="seat-legend-item">
                              <div className="legend-seat ladies"></div>
                              <span>Ladies Seat</span>
                            </div>
                            <div className="seat-legend-item">
                              <div className="legend-seat sleeper"></div>
                              <span>Sleeper</span>
                            </div>
                          </div>
                        </div>

                        {/* Seat Type Info */}
                        {Object.keys(seatTypes).length > 0 && (
                          <div className="seat-type-info mb-4">
                            <div className="seat-type-title">
                              <RiInformationLine className="me-2" />
                              Available Seat Types
                            </div>
                            <div className="seat-type-items">
                              {seatTypes.SEATER && (
                                <div className="seat-type-item">
                                  <span className="seat-type-badge seater">
                                    S
                                  </span>
                                  <span>Seater: {seatTypes.SEATER} seats</span>
                                </div>
                              )}
                              {seatTypes.LOWER_SLEEPER && (
                                <div className="seat-type-item">
                                  <span className="seat-type-badge lower-sleeper">
                                    LS
                                  </span>
                                  <span>
                                    Lower Sleeper: {seatTypes.LOWER_SLEEPER}{" "}
                                    seats
                                  </span>
                                </div>
                              )}
                              {seatTypes.UPPER_SLEEPER && (
                                <div className="seat-type-item">
                                  <span className="seat-type-badge upper-sleeper">
                                    US
                                  </span>
                                  <span>
                                    Upper Sleeper: {seatTypes.UPPER_SLEEPER}{" "}
                                    seats
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Seat Layout Component */}
                        <div className="seat-layout-container">
                          <BusLayout
                            seatData={seatLayoutData}
                            onSeatSelect={handleSeatSelect}
                            selectedSeats={selectedSeats}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Passenger Details Forms */}
                {selectedSeats.length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                          <RiUserLine className="me-2" />
                          Passenger Details ({passengerCount}{" "}
                          {passengerCount === 1 ? "Passenger" : "Passengers"})
                        </h5>
                        {selectedSeats.length !== passengerDetails.length && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={syncSeatsWithForms}
                          >
                            <RiUserAddLine className="me-1" />
                            Sync Forms ({selectedSeats.length})
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="card-body">
                      {/* Sync Warning */}
                      {selectedSeats.length !== passengerDetails.length && (
                        <div className="alert alert-warning mb-4">
                          <div className="d-flex align-items-center">
                            <RiErrorWarningLine className="me-2 fs-5" />
                            <div>
                              <strong>Seats and forms count don't match</strong>
                              <div className="small">
                                Seats selected: {selectedSeats.length} | Forms:{" "}
                                {passengerDetails.length}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Passenger Forms Grid */}
                      <div className="passenger-forms-grid">
                        {passengerDetails.map((passenger, index) => (
                          <div key={index} className="passenger-form-card">
                            <div className="passenger-form-header">
                              <div className="passenger-number">
                                Passenger {index + 1}
                              </div>
                              <div className="passenger-seat-info">
                                {selectedSeats[index] ? (
                                  <span className="seat-badge">
                                    Seat: {selectedSeats[index].id}
                                  </span>
                                ) : (
                                  <span className="text-warning">
                                    No seat assigned
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="passenger-form-body">
                              <div className="row g-3">
                                {/* Title */}
                                <div className="col-md-2">
                                  <div className="form-group">
                                    <label className="form-label">
                                      Title *
                                    </label>
                                    <select
                                      className="form-select"
                                      value={passenger.title}
                                      onChange={(e) =>
                                        handlePassengerDetailChange(
                                          index,
                                          "title",
                                          e.target.value
                                        )
                                      }
                                      required
                                    >
                                      <option value="Mr">Mr</option>
                                      <option value="Ms">Ms</option>
                                      <option value="Mrs">Mrs</option>
                                      <option value="Master">Master</option>
                                    </select>
                                  </div>
                                </div>

                                {/* First Name */}
                                <div className="col-md-5">
                                  <div className="form-group">
                                    <label className="form-label">
                                      First Name *
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="First name"
                                      value={passenger.firstName}
                                      onChange={(e) =>
                                        handlePassengerDetailChange(
                                          index,
                                          "firstName",
                                          e.target.value
                                        )
                                      }
                                      required
                                    />
                                  </div>
                                </div>

                                {/* Last Name */}
                                <div className="col-md-5">
                                  <div className="form-group">
                                    <label className="form-label">
                                      Last Name *
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Last name"
                                      value={passenger.lastName}
                                      onChange={(e) =>
                                        handlePassengerDetailChange(
                                          index,
                                          "lastName",
                                          e.target.value
                                        )
                                      }
                                      required
                                    />
                                  </div>
                                </div>

                                {/* Age */}
                                <div className="col-md-4">
                                  <div className="form-group">
                                    <label className="form-label">Age *</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      placeholder="Age"
                                      min="1"
                                      max="100"
                                      value={passenger.age}
                                      onChange={(e) =>
                                        handlePassengerDetailChange(
                                          index,
                                          "age",
                                          e.target.value
                                        )
                                      }
                                      required
                                    />
                                  </div>
                                </div>

                                {/* ID Proof */}
                                <div className="col-md-4">
                                  <div className="form-group">
                                    <label className="form-label">
                                      ID Proof *
                                    </label>
                                    <select
                                      className="form-select"
                                      value={passenger.idProof}
                                      onChange={(e) =>
                                        handlePassengerDetailChange(
                                          index,
                                          "idProof",
                                          e.target.value
                                        )
                                      }
                                      required
                                    >
                                      <option value="">Select ID Proof</option>
                                      <option value="AADHAR">
                                        Aadhar Card
                                      </option>
                                      <option value="PAN">PAN Card</option>
                                      <option value="DL">
                                        Driving License
                                      </option>
                                      <option value="VOTER">Voter ID</option>
                                      <option value="PASSPORT">Passport</option>
                                    </select>
                                  </div>
                                </div>

                                {/* ID Number */}
                                <div className="col-md-4">
                                  <div className="form-group">
                                    <label className="form-label">
                                      ID Number
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="ID Number"
                                      value={passenger.idProofNumber}
                                      onChange={(e) =>
                                        handlePassengerDetailChange(
                                          index,
                                          "idProofNumber",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Common Contact Information */}
                      <div className="common-contact-section mt-4">
                        <h6 className="common-contact-title">
                          <RiUserLine className="me-2" />
                          Customer Information
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="form-label">
                                Email Address *
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                placeholder="Enter email address"
                                value={passengerDetails[0]?.email || ""}
                                onChange={(e) =>
                                  handleEmailChange(e.target.value)
                                }
                                required
                              />
                              <div className="form-text">
                                Booking confirmation will be sent to this email
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label className="form-label">
                                Phone Number *
                              </label>
                              <input
                                type="tel"
                                className="form-control"
                                placeholder="Enter phone number"
                                value={passengerDetails[0]?.phone || ""}
                                onChange={(e) =>
                                  handlePhoneChange(e.target.value)
                                }
                                required
                              />
                              <div className="form-text">
                                Important updates will be sent via SMS
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar - Booking Summary */}
              <div className="col-lg-4">
                <div className="sticky-top" style={{ top: "20px" }}>
                  {/* Summary Card */}
                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="mb-0">Booking Summary</h5>
                    </div>
                    <div className="card-body">
                      {/* Boarding Points Section */}
                      {selectedSeats.length > 0 && (
                        <div className="boarding-points-section mb-4">
                          <div className="boarding-points-header">
                            <h6 className="mb-0 d-flex align-items-center">
                              <RiMapPinTimeLine className="me-2" />
                              Boarding Points
                            </h6>
                          </div>

                          {selectedBoardingPoint && selectedDroppingPoint ? (
                            <div className="selected-points-details">
                              <div className="point-item">
                                <div className="point-label">
                                  Boarding Point
                                </div>
                                <div className="point-value">
                                  {selectedBoardingPoint.pointName ||
                                    selectedBoardingPoint.location}
                                </div>
                                <div className="point-time">
                                  {formatTime(selectedBoardingPoint.time)}
                                </div>
                              </div>
                              <div className="point-arrow">
                                <RiArrowLeftLine className="rotate-90" />
                              </div>
                              <div className="point-item">
                                <div className="point-label">
                                  Dropping Point
                                </div>
                                <div className="point-value">
                                  {selectedDroppingPoint.pointName ||
                                    selectedDroppingPoint.location}
                                </div>
                                <div className="point-time">
                                  {formatTime(selectedDroppingPoint.time)}
                                </div>
                              </div>
                              <button
                                className="btn btn-outline-primary btn-sm mt-2"
                                onClick={handleChangeBoardingPoints}
                              >
                                <RiEditLine className="me-1" />
                                Change
                              </button>
                            </div>
                          ) : (
                            <div className="no-points-selected">
                              <p className="text-muted mb-2">
                                Please select boarding and dropping points
                              </p>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => setShowBoardingPoints(true)}
                              >
                                Select Points
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Selected Seats */}
                      <div className="selected-seats-section mb-4">
                        <h6>Selected Seats</h6>
                        {selectedSeats.length > 0 ? (
                          <div className="selected-seats-list">
                            {selectedSeats.map((seat, index) => {
                              const isUpper = seat.SeatType === 1;
                              const isSleeper = seat.sleeper;
                              const seatBaseFare =
                                seat.totalPriceWithTaxes || 0;
                              const seatConvenienceFee =
                                seat.convenienceFee || 100;
                              const seatTotal =
                                seatBaseFare + seatConvenienceFee;
                              const passengerName = passengerDetails[index]
                                ? `${passengerDetails[index].firstName} ${passengerDetails[index].lastName}`
                                : `Passenger ${index + 1}`;

                              return (
                                <div
                                  key={seat.id}
                                  className="selected-seat-item"
                                >
                                  <div className="seat-info">
                                    <div className="seat-id">{seat.id}</div>
                                    <div className="seat-details">
                                      <div className="seat-type">
                                        {isSleeper
                                          ? isUpper
                                            ? "Upper Sleeper"
                                            : "Lower Sleeper"
                                          : "Seater"}
                                      </div>
                                      <div className="passenger-name">
                                        {passengerName}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="seat-actions">
                                    <div className="seat-price">
                                      {formatCurrency(seatTotal)}
                                    </div>
                                    <button
                                      className="seat-remove-btn"
                                      onClick={() => handleSeatSelect(seat)}
                                    >
                                      <RiCloseLine />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-seats-selected">
                            <p className="text-muted mb-0">
                              No seats selected yet
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Fare Breakdown */}
                      {selectedSeats.length > 0 && (
                        <div className="fare-breakdown-section">
                          <h6>Fare Breakdown</h6>
                          <div className="fare-breakdown">
                            <div className="fare-item">
                              <div className="fare-label">Bus Fare</div>
                              <div className="fare-amount">
                                {formatCurrency(fareSummary.baseFare)}
                              </div>
                            </div>
                            <div className="fare-item">
                              <div className="fare-label">Convenience Fee</div>
                              <div className="fare-amount">
                                {formatCurrency(fareSummary.convenienceFee)}
                              </div>
                            </div>
                            <div className="fare-divider"></div>
                            <div className="fare-item total">
                              <div className="fare-label">Total Amount</div>
                              <div className="fare-amount">
                                {formatCurrency(fareSummary.total)}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Proceed Button */}
                      <div className="proceed-section mt-4">
                        {selectedSeats.length === 0 ? (
                          <button className="btn btn-secondary w-100" disabled>
                            Select Seats to Continue
                          </button>
                        ) : selectedSeats.length !== passengerDetails.length ? (
                          <button
                            className="btn btn-warning w-100"
                            onClick={syncSeatsWithForms}
                          >
                            Sync Seats & Forms
                          </button>
                        ) : !areAllPassengerDetailsFilled() ? (
                          <button className="btn btn-secondary w-100" disabled>
                            Fill Passenger Details
                          </button>
                        ) : !selectedBoardingPoint || !selectedDroppingPoint ? (
                          <button
                            className="btn btn-primary w-100"
                            onClick={() => setShowBoardingPoints(true)}
                          >
                            Select Boarding Points
                          </button>
                        ) : (
                          <button
                            className="btn btn-success w-100"
                            onClick={handleProceedToPayment}
                          >
                            Continue to Payment
                          </button>
                        )}
                      </div>

                      {/* Warning Messages */}
                      {selectedSeats.length > 0 &&
                        selectedSeats.length !== passengerDetails.length && (
                          <div className="alert alert-warning mt-3">
                            <RiErrorWarningLine className="me-2" />
                            {selectedSeats.length > passengerDetails.length
                              ? `Missing ${
                                  selectedSeats.length - passengerDetails.length
                                } passenger form${
                                  selectedSeats.length -
                                    passengerDetails.length >
                                  1
                                    ? "s"
                                    : ""
                                }`
                              : `Extra ${
                                  passengerDetails.length - selectedSeats.length
                                } passenger form${
                                  passengerDetails.length -
                                    selectedSeats.length >
                                  1
                                    ? "s"
                                    : ""
                                }`}
                          </div>
                        )}

                      {selectedSeats.length > 0 &&
                        selectedSeats.length === passengerDetails.length &&
                        !areAllPassengerDetailsFilled() && (
                          <div className="alert alert-warning mt-3">
                            <RiErrorWarningLine className="me-2" />
                            Please fill all passenger details to continue
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Boarding Points Modal */}
      {showBoardingPoints && (
        <BusBoardingPoints
          bus={bus}
          onClose={handleBoardingPointsClose}
          onSubmit={handleBoardingPointsSubmit}
          autoProceed={false}
        />
      )}

      {/* CSS Styles */}
      <style jsx>{`
        /* Bus Info Styles */
        .bus-info-item {
          display: flex;
          align-items: center;
          padding: 10px;
          border-radius: 8px;
          background: #f8f9fa;
        }

        .bus-info-icon {
          width: 40px;
          height: 40px;
          background: #4361ee;
          color: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-right: 12px;
        }

        .bus-info-label {
          font-size: 12px;
          color: #6c757d;
          margin-bottom: 2px;
        }

        .bus-info-value {
          font-weight: 600;
          font-size: 14px;
        }

        /* Passenger Controls */
        .passenger-controls {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .passenger-control-btn {
          width: 30px;
          height: 30px;
          border: 1px solid #dee2e6;
          background: white;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .passenger-control-btn:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #4361ee;
        }

        .passenger-control-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .passenger-count {
          font-weight: 600;
          font-size: 16px;
          min-width: 30px;
          text-align: center;
        }

        /* Selection Badge */
        .selection-badge {
          padding: 4px 12px;
          background: #4361ee;
          color: white;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
        }

        /* Seat Legend */
        .seat-legend {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .seat-legend-title {
          font-weight: 600;
          margin-bottom: 10px;
          color: #495057;
        }

        .seat-legend-items {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
        }

        .seat-legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .legend-seat {
          width: 24px;
          height: 24px;
          border: 2px solid #dee2e6;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
        }

        .legend-seat.available {
          background: #e9ecef;
        }

        .legend-seat.selected {
          background: #20c997;
          color: white;
          border-color: #20c997;
        }

        .legend-seat.booked {
          background: #dc3545;
          color: white;
          border-color: #dc3545;
        }

        .legend-seat.ladies {
          background: #ffc0cb;
          border-color: #ffc0cb;
        }

        .legend-seat.sleeper {
          background: #4361ee;
          color: white;
          border-color: #4361ee;
        }

        /* Seat Type Info */
        .seat-type-info {
          background: #e7f5ff;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .seat-type-title {
          font-weight: 600;
          margin-bottom: 10px;
          color: #0c63e4;
        }

        .seat-type-items {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
        }

        .seat-type-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .seat-type-badge {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
          color: white;
        }

        .seat-type-badge.seater {
          background: #4361ee;
        }

        .seat-type-badge.lower-sleeper {
          background: #20c997;
        }

        .seat-type-badge.upper-sleeper {
          background: #ff6b6b;
        }

        /* Seat Layout Container */
        .seat-layout-container {
          background: white;
          padding: 20px;
          border-radius: 10px;
          border: 1px solid #dee2e6;
          overflow: auto;
        }

        /* Passenger Forms */
        .passenger-forms-grid {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .passenger-form-card {
          border: 1px solid #e9ecef;
          border-radius: 10px;
          padding: 20px;
          background: #f8f9fa;
        }

        .passenger-form-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 1px solid #dee2e6;
        }

        .passenger-number {
          font-weight: 600;
          font-size: 16px;
          color: #495057;
        }

        .passenger-seat-info .seat-badge {
          padding: 4px 8px;
          background: #4361ee;
          color: white;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 0;
        }

        .form-label {
          font-size: 12px;
          font-weight: 500;
          color: #495057;
          margin-bottom: 4px;
        }

        .form-text {
          font-size: 11px;
          color: #6c757d;
          margin-top: 4px;
        }

        /* Common Contact Section */
        .common-contact-section {
          background: #e7f5ff;
          padding: 20px;
          border-radius: 10px;
        }

        .common-contact-title {
          font-weight: 600;
          margin-bottom: 15px;
          color: #0c63e4;
        }

        /* Boarding Points Section */
        .boarding-points-section {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
        }

        .selected-points-details {
          margin-top: 10px;
        }

        .point-item {
          background: white;
          padding: 10px;
          border-radius: 6px;
          margin-bottom: 10px;
          border: 1px solid #e9ecef;
        }

        .point-label {
          font-size: 11px;
          color: #6c757d;
          margin-bottom: 2px;
        }

        .point-value {
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 4px;
        }

        .point-time {
          font-size: 12px;
          color: #0ca678;
        }

        .point-arrow {
          text-align: center;
          margin: 5px 0;
          color: #6c757d;
        }

        .point-arrow svg {
          transform: rotate(90deg);
        }

        .no-points-selected {
          text-align: center;
          padding: 20px 0;
        }

        /* Selected Seats */
        .selected-seats-list {
          max-height: 200px;
          overflow-y: auto;
        }

        .selected-seat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          margin-bottom: 8px;
        }

        .seat-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .seat-id {
          width: 30px;
          height: 30px;
          background: #4361ee;
          color: white;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .seat-details {
          display: flex;
          flex-direction: column;
        }

        .seat-type {
          font-size: 12px;
          color: #6c757d;
        }

        .passenger-name {
          font-size: 13px;
          font-weight: 500;
        }

        .seat-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .seat-price {
          font-weight: 600;
          color: #0ca678;
        }

        .seat-remove-btn {
          width: 24px;
          height: 24px;
          border: 1px solid #dc3545;
          background: white;
          color: #dc3545;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .seat-remove-btn:hover {
          background: #dc3545;
          color: white;
        }

        .no-seats-selected {
          text-align: center;
          padding: 20px;
          color: #6c757d;
        }

        /* Fare Breakdown */
        .fare-breakdown {
          background: white;
          padding: 15px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .fare-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .fare-label {
          color: #495057;
        }

        .fare-subtext {
          font-size: 11px;
          color: #6c757d;
          margin-top: 2px;
        }

        .fare-amount {
          font-weight: 500;
        }

        .fare-divider {
          border-top: 1px dashed #dee2e6;
          margin: 10px 0;
        }

        .fare-item.total {
          border-top: 2px solid #dee2e6;
          padding-top: 10px;
          margin-top: 10px;
          margin-bottom: 0;
        }

        .fare-item.total .fare-label {
          font-weight: 600;
        }

        .fare-item.total .fare-amount {
          font-weight: 600;
          font-size: 18px;
          color: #0ca678;
        }

        /* Proceed Button */
        .proceed-section .btn {
          padding: 12px;
          font-weight: 600;
          font-size: 16px;
          border-radius: 8px;
        }

        /* Important Info List */
        .important-info-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .important-info-list li {
          padding: 6px 0;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
          color: #495057;
        }

        .important-info-list li:last-child {
          border-bottom: none;
        }

        /* Sticky Sidebar */
        .sticky-top {
          position: sticky;
          z-index: 100;
        }

        /* Responsive Styles */
        @media (max-width: 768px) {
          .bus-info-item {
            margin-bottom: 15px;
          }

          .seat-legend-items {
            justify-content: center;
          }

          .seat-type-items {
            justify-content: center;
          }

          .sticky-top {
            position: static;
          }

          .passenger-form-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .selected-points-details {
            text-align: center;
          }

          .point-arrow {
            margin: 10px 0;
          }
        }

        @media (max-width: 576px) {
          .bus-info-item {
            flex-direction: column;
            text-align: center;
          }

          .bus-info-icon {
            margin-right: 0;
            margin-bottom: 8px;
          }

          .passenger-controls {
            justify-content: center;
          }

          .selection-badge {
            font-size: 12px;
            padding: 3px 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default BusSeatSelection;
